import { useEffect, useState } from 'react'
import { Car, Building2, Calendar, Tags, Layers, Fuel, ListChecks, Truck, FileText, FileSpreadsheet } from 'lucide-react'
import api from '../services/api'
import LookupSelect from '../components/LookupSelect'
import { useEmpresa } from '../contexts/EmpresaContext'
import { Linha } from './LancamentoDepositos'

interface Mes { ano: number; mes: number; valor: number; litros: number; percurso: number; media: number | null }
interface Veiculo {
  veicod: string; veipla: string; tipo: string; modelo: string; motorista: string; combustivel: string
  meses: Mes[]; total: { valor: number; litros: number; percurso: number; media: number | null }; mediaKm: number
}
interface Resultado { meses: string[]; veiculos: Veiculo[] }
interface Opcao { cod: string; des: string }

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']
const fmt = (v: number, casas: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })
// Igual ao ERP: fora da faixa sai como ***,**.
const fmtMedia = (v: number | null) => (v == null ? '—' : v > 999.99 ? '***,**' : fmt(v, 2))
const rotuloMes = (ym: string) => { const [a, m] = ym.split('-').map(Number); return `${MESES[m - 1].slice(0, 3)}/${a}` }

// Consumo mensal de combustível por veículo (GeneXus WOCOMMES → RDPS032 "Normal" / RDPS037x "KM").
export default function RelatorioConsumoMensalVeiculo() {
  const { empresas } = useEmpresa()
  const hoje = new Date()
  const [veiculoDe, setVeiculoDe] = useState('')
  const [veiculoAte, setVeiculoAte] = useState('')
  const [modelo, setModelo] = useState('')
  const [empresa, setEmpresa] = useState('0')
  const [mesIni, setMesIni] = useState(hoje.getMonth() + 1)
  const [anoIni, setAnoIni] = useState(hoje.getFullYear())
  const [mesFim, setMesFim] = useState(hoje.getMonth() + 1)
  const [anoFim, setAnoFim] = useState(hoje.getFullYear())
  const [tipo, setTipo] = useState('')
  const [combustivel, setCombustivel] = useState('')
  const [tipoRel, setTipoRel] = useState<'N' | 'K'>('N')

  const [tipos, setTipos] = useState<Opcao[]>([])
  const [modelos, setModelos] = useState<Opcao[]>([])
  const [combustiveis, setCombustiveis] = useState<Opcao[]>([])
  const [res, setRes] = useState<Resultado | null>(null)
  const [resTipo, setResTipo] = useState<'N' | 'K'>('N') // layout do resultado na tela = tipo com que foi gerado
  const [carregando, setCarregando] = useState(false)
  const [gerando, setGerando] = useState<'' | 'pdf' | 'excel'>('')
  const [erro, setErro] = useState('')

  useEffect(() => {
    api.get('/combustivel/tipos-veiculo').then(r => setTipos(r.data)).catch(() => {})
    api.get('/combustivel/modelos-veiculo').then(r => setModelos(r.data)).catch(() => {})
    api.get('/combustivel/combustiveis-veiculo').then(r => setCombustiveis(r.data)).catch(() => {})
  }, [])

  const params = () => ({
    veiculoDe, veiculoAte, modelo, empresa, tipo, combustivel, tipoRel,
    inicio: `${anoIni}-${String(mesIni).padStart(2, '0')}`,
    fim: `${anoFim}-${String(mesFim).padStart(2, '0')}`,
  })

  async function gerar() {
    setErro(''); setCarregando(true)
    try { setRes((await api.get('/combustivel/relatorios/consumo-mensal', { params: params() })).data); setResTipo(tipoRel) }
    catch (e: any) { setRes(null); setErro(e?.response?.data?.error || 'Erro ao gerar relatório.') }
    finally { setCarregando(false) }
  }

  async function exportar(formato: 'pdf' | 'excel') {
    setErro(''); setGerando(formato)
    try {
      const r = await api.get(`/combustivel/relatorios/consumo-mensal/${formato}`, { params: params(), responseType: 'blob' })
      const tipoArq = formato === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      const url = URL.createObjectURL(new Blob([r.data], { type: tipoArq }))
      if (formato === 'pdf') window.open(url, '_blank')
      else {
        const a = document.createElement('a'); a.href = url
        a.download = tipoRel === 'K' ? 'percursos-mensais-veiculos.xlsx' : 'consumo-mensal-veiculos.xlsx'
        a.click(); URL.revokeObjectURL(url)
      }
    } catch (e: any) {
      // Erro de validação vem como blob JSON.
      try { const t = await e?.response?.data?.text?.(); setErro(JSON.parse(t).error) } catch { setErro(formato === 'pdf' ? 'Erro ao gerar PDF.' : 'Erro ao gerar Excel.') }
    } finally { setGerando('') }
  }

  const seletorMes = (mes: number, setMes: (m: number) => void, ano: number, setAno: (a: number) => void) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <select value={mes} onChange={e => setMes(Number(e.target.value))} style={{ maxWidth: 140 }}>
        {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
      </select>
      <span style={{ color: '#57606f' }}>/</span>
      <input type="number" value={ano} min={2000} max={2100} onChange={e => setAno(Number(e.target.value))} style={{ width: 90 }} />
    </div>
  )

  const th: React.CSSProperties = { padding: '0.4rem 0.5rem', fontSize: '0.66rem', fontWeight: 800, color: '#fff', background: '#1a6ea8', textAlign: 'right', whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 1 }
  const td: React.CSSProperties = { padding: '0.32rem 0.5rem', fontSize: '0.78rem', borderBottom: '1px solid #eef2f7', textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }
  const kmDoMes = (v: Veiculo, ym: string) => { const [a, m] = ym.split('-').map(Number); return v.meses.find(x => x.ano === a && x.mes === m)?.percurso ?? 0 }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
      <div className="modal-box" style={{ width: '100%', maxWidth: 680, maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1a6ea8, #14547f)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 9, background: 'rgba(255,255,255,0.15)' }}>
              <Truck size={18} color="#fff" />
            </span>
            <div>
              <h2>Consumo Mensal por Veículo</h2>
              <div style={{ fontSize: '0.7rem', color: '#dbeafe', marginTop: 2 }}>Valor, litros, percurso e média de cada veículo por mês</div>
            </div>
          </div>
        </div>

        <div className="modal-corpo">
          <Linha label="Veículos" icon={Car}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <div style={{ maxWidth: 140 }}>
                <LookupSelect endpoint="/erp/veiculos" codKey="veicod" descKey="veipla" tagKey="veicod"
                  codValue={veiculoDe} descValue={veiculoDe} onSelect={cod => setVeiculoDe(cod)} placeholder="Primeiro" />
              </div>
              <span style={{ fontSize: '0.8rem', color: '#57606f' }}>a</span>
              <div style={{ maxWidth: 140 }}>
                <LookupSelect endpoint="/erp/veiculos" codKey="veicod" descKey="veipla" tagKey="veicod"
                  codValue={veiculoAte} descValue={veiculoAte} onSelect={cod => setVeiculoAte(cod)} placeholder="Último" />
              </div>
              {(veiculoDe || veiculoAte) && (
                <button className="btn btn-secondary" style={{ padding: '0.25rem 0.6rem', fontSize: '0.72rem' }} onClick={() => { setVeiculoDe(''); setVeiculoAte('') }}>Todos</button>
              )}
            </div>
          </Linha>
          <Linha label="Modelo" icon={Layers}>
            <select value={modelo} onChange={e => setModelo(e.target.value)} style={{ maxWidth: 280 }}>
              <option value="">Todos</option>
              {modelos.map(m => <option key={m.cod} value={m.cod}>{m.des}</option>)}
            </select>
          </Linha>
          <Linha label="Empresa" icon={Building2}>
            <select value={empresa} onChange={e => setEmpresa(e.target.value)} style={{ maxWidth: 280 }}>
              <option value="0">Todas</option>
              {empresas.map(e => <option key={e.empcod} value={e.empcod}>{e.empcod} — {e.empfan?.trim() || e.empnom?.trim()}</option>)}
            </select>
          </Linha>
          <Linha label="Início" icon={Calendar}>{seletorMes(mesIni, setMesIni, anoIni, setAnoIni)}</Linha>
          <Linha label="Fim" icon={Calendar}>{seletorMes(mesFim, setMesFim, anoFim, setAnoFim)}</Linha>
          <Linha label="Tipo" icon={Tags}>
            <select value={tipo} onChange={e => setTipo(e.target.value)} style={{ maxWidth: 280 }}>
              <option value="">Todos</option>
              {tipos.map(t => <option key={t.cod} value={t.cod}>{t.des}</option>)}
            </select>
          </Linha>
          <Linha label="Combustível" icon={Fuel}>
            <select value={combustivel} onChange={e => setCombustivel(e.target.value)} style={{ maxWidth: 200 }}>
              <option value="">Todos</option>
              {combustiveis.map(c => <option key={c.cod} value={c.cod}>{c.des}</option>)}
            </select>
          </Linha>
          <Linha label="Tipo Relatório" icon={ListChecks}>
            <select value={tipoRel} onChange={e => setTipoRel(e.target.value as 'N' | 'K')} style={{ maxWidth: 200 }}>
              <option value="N">Normal</option>
              <option value="K">KM</option>
            </select>
            {tipoRel === 'K' && <div style={{ fontSize: '0.72rem', marginTop: 4, color: '#94a3b8' }}>Km rodados por mês (até 12 meses), com a média dos meses rodados.</div>}
          </Linha>
          {erro && (
            <div style={{ marginTop: '0.9rem', fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>{erro}</div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-primary" onClick={gerar} disabled={carregando}>{carregando ? 'Gerando...' : 'Gerar'}</button>
          <button className="btn" style={{ background: '#dc2626', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }} onClick={() => exportar('pdf')} disabled={!!gerando}>
            <FileText size={15} /> {gerando === 'pdf' ? 'Gerando PDF...' : 'PDF'}
          </button>
          <button className="btn" style={{ background: '#15803d', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }} onClick={() => exportar('excel')} disabled={!!gerando}>
            <FileSpreadsheet size={15} /> {gerando === 'excel' ? 'Gerando Excel...' : 'Excel'}
          </button>
        </div>
      </div>

      {res && (
        <div className="modal-box" style={{ width: '100%', maxWidth: 'none', maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
          <div className="modal-corpo" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ fontSize: '0.8rem', color: '#475569' }}><b>{res.veiculos.length}</b> veículo(s)</div>
            {res.veiculos.length === 0 && <div style={{ textAlign: 'center', color: '#94a3b8', padding: '1.2rem' }}>Nenhum consumo no período.</div>}

            {resTipo === 'K' && res.veiculos.length > 0 && (
              <div style={{ overflow: 'auto', maxHeight: '70vh', border: '1px solid #bfdbfe', borderRadius: 8 }}>
                <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ ...th, textAlign: 'left' }}>Veículo</th>
                      <th style={{ ...th, textAlign: 'left' }}>Placa</th>
                      {res.meses.map(ym => <th key={ym} style={th}>{rotuloMes(ym)}</th>)}
                      <th style={{ ...th, background: '#0f2a52' }}>Média Geral</th>
                    </tr>
                  </thead>
                  <tbody>
                    {res.veiculos.map((v, i) => (
                      <tr key={v.veicod} style={{ background: i % 2 ? '#f5f9fd' : '#fff' }}>
                        <td style={{ ...td, textAlign: 'left', fontWeight: 700, color: '#1a6ea8' }}>{v.veicod}</td>
                        <td style={{ ...td, textAlign: 'left', color: '#b91c1c' }}>{v.veipla}</td>
                        {res.meses.map(ym => { const km = kmDoMes(v, ym); return <td key={ym} style={{ ...td, color: km ? '#111827' : '#cbd5e1' }}>{fmt(km, 0)}</td> })}
                        <td style={{ ...td, fontWeight: 800, color: '#1b3f7b' }}>{fmt(v.mediaKm, 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {resTipo === 'N' && res.veiculos.map(v => (
              <div key={v.veicod} style={{ border: '1px solid #bfdbfe', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
                <div style={{ padding: '0.5rem 0.8rem', fontSize: '0.8rem', background: '#f5f9fd', display: 'flex', gap: '1.5rem', flexWrap: 'wrap', color: '#1b3f7b' }}>
                  <span><b>Veículo:</b> {v.veicod}</span>
                  <span><b>Placa:</b> {v.veipla}</span>
                  <span><b>Tipo:</b> {v.tipo}</span>
                  <span><b>Modelo:</b> {v.modelo}</span>
                  <span><b>Motorista:</b> {v.motorista || '—'}</span>
                  <span><b>Combustível:</b> {v.combustivel || '—'}</span>
                </div>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ ...th, position: 'static', textAlign: 'left' }}>Ano / Mês</th>
                      <th style={{ ...th, position: 'static' }}>Valor</th>
                      <th style={{ ...th, position: 'static' }}>Litros</th>
                      <th style={{ ...th, position: 'static' }}>Percurso</th>
                      <th style={{ ...th, position: 'static' }}>Média (km/l)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {v.meses.map(m => (
                      <tr key={`${m.ano}-${m.mes}`}>
                        <td style={{ ...td, textAlign: 'left' }}>{m.ano} / {MESES[m.mes - 1]}</td>
                        <td style={td}>{fmt(m.valor, 2)}</td>
                        <td style={td}>{fmt(m.litros, 3)}</td>
                        <td style={td}>{fmt(m.percurso, 0)}</td>
                        <td style={td}>{fmtMedia(m.media)}</td>
                      </tr>
                    ))}
                    <tr style={{ background: '#eff6ff' }}>
                      <td style={{ ...td, textAlign: 'left', fontWeight: 800, color: '#1b3f7b' }}>Total Período</td>
                      <td style={{ ...td, fontWeight: 800, color: '#1b3f7b' }}>{fmt(v.total.valor, 2)}</td>
                      <td style={{ ...td, fontWeight: 800, color: '#1b3f7b' }}>{fmt(v.total.litros, 3)}</td>
                      <td style={{ ...td, fontWeight: 800, color: '#1b3f7b' }}>{fmt(v.total.percurso, 0)}</td>
                      <td style={{ ...td, fontWeight: 800, color: '#1b3f7b' }}>{fmtMedia(v.total.media)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
